import { z } from 'zod';
import { createModel, partySchema, QueryMany, QueryOne } from '../../common';
import { businessSchema, productSchema } from '../../common/app-registry.schema';
import { currencySchema } from '../../common/currency.schema';
import {
  dateSchema,
  nameSchema,
  objectIdSchema,
  positiveIntegerSchema,
  priceSchema,
} from '../../validation';
import {
  referralRewardPartSchema,
  referralRewardTriggerSchema,
} from './referral.reward-rule.model';

/**
 * Lifecycle of one reward row.
 *
 * - `pending`   — earned, not yet settled. Cash parts wait for finance to
 *                 mark them paid; credit parts are granted immediately on
 *                 creation and the row moves straight to `granted` when
 *                 every part is a credit.
 * - `granted`   — every part settled (credits landed, cash marked paid).
 * - `void`      — reversed: the Billing was refunded before payout, or an
 *                 admin cancelled it. Credits already granted are NOT
 *                 clawed back automatically; `voidReason` says why.
 */
export const referralRewardStatusSchema = z.enum(['pending', 'granted', 'void']);
export type ReferralRewardStatus = z.infer<typeof referralRewardStatusSchema>;

/**
 * Per-part settlement state. Mirrors one `ReferralRewardPart` from the
 * rule with what it actually produced for this Billing.
 */
export const referralRewardPartResultSchema = z.object({
  /** The rule part that produced this, as it was at evaluation time. */
  part: referralRewardPartSchema,
  /**
   * - `pending`  — cash owed, awaiting payout.
   * - `granted`  — credits landed / cash paid.
   * - `skipped`  — could not apply (e.g. `order_credits` on a partner
   *                with no `owner`); `note` says why.
   */
  status: z.enum(['pending', 'granted', 'skipped']),
  /** Computed cash for `amount_*` parts, in `currency`. */
  amount: priceSchema.optional(),
  currency: currencySchema.optional(),
  /** Units granted for `order_credits`. */
  units: positiveIntegerSchema.optional(),
  /** `Subscription._id` the credits were granted into. */
  subscriptionId: objectIdSchema.optional(),
  /** When the part settled. */
  settledAt: dateSchema.optional(),
  /** Human note — why skipped, payout reference, … */
  note: z.string().trim().optional(),
});
export type ReferralRewardPartResult = z.infer<typeof referralRewardPartResultSchema>;

/**
 * `ReferralReward` collection — one row per event the reward rule fired
 * on: a settled Billing (`first_paid` / `every_paid`, `billingId` set)
 * or a deduped click hit (`per_click`, `trackingId` set). The partner's
 * payout ledger.
 *
 * Created by `ReferralRewardService.onBillingSettled` when a Billing
 * whose payer carries a referral attribution settles, and by
 * `onTrackingClick` when a new click hit lands on a pay-per-click link.
 * Idempotent on `billingId` / `trackingId` — a re-delivered webhook or a
 * second `settled` transition never creates a second row.
 *
 * Expected indexes (created by the `referral-collections` migration):
 * - `{ billingId: 1 }` unique, partial on existence — idempotency.
 * - `{ trackingId: 1 }` unique, partial on existence — idempotency.
 * - `{ partnerId: 1, status: 1, 'doc.createdAt': -1 }` — payout list.
 * - `{ linkId: 1, 'doc.createdAt': -1 }` — per-link rewards.
 * - `{ 'referred._id': 1, trigger: 1 }` — "has this account already
 *   produced a `first_paid` reward".
 */
export const ReferralRewardSchema = createModel({
  /** `ReferralPartner._id` being paid. */
  partnerId: objectIdSchema,
  /** `ReferralLink._id` the referred account was attributed through. */
  linkId: objectIdSchema,
  /** The link's code at evaluation time, for the ledger. */
  code: nameSchema,
  /**
   * The account that paid — the Billing's payer (Org / User / Driver).
   * Absent on `per_click` rows: a click is anonymous.
   */
  referred: partySchema.optional(),
  /** The `Lead._id` the attribution came through, when there was one. */
  leadId: objectIdSchema.optional(),
  /** The settled `Billing._id` that fired the rule (`first_paid` / `every_paid`). */
  billingId: objectIdSchema.optional(),
  /** The `ReferralTracking._id` click hit that fired the rule (`per_click`). */
  trackingId: objectIdSchema.optional(),
  /** Snapshot of what was bought, for reports and product filters. */
  purchase: z
    .object({
      business: businessSchema,
      product: productSchema,
      planCode: z.string().optional(),
      /** `Billing.amount.subtotal` (pre-tax), the base for `amount_percent`. */
      subtotal: priceSchema,
      currency: currencySchema,
    })
    .optional(),
  /** Which trigger fired — copied from the rule so a later rule edit does not rewrite history. */
  trigger: referralRewardTriggerSchema,
  /** One result per rule part. */
  parts: z.array(referralRewardPartResultSchema).default([]),
  /**
   * Sum of `parts[].amount` for cash parts, in `currency`. `0` when the
   * rule is credits-only. Kept so the payout list can sum without
   * unwinding `parts`.
   */
  totalAmount: priceSchema.default(0),
  currency: currencySchema.default('SAR'),
  /** Roll-up of `parts[].status`. See {@link referralRewardStatusSchema}. */
  status: referralRewardStatusSchema.default('pending'),
  /** When every part settled. */
  grantedAt: dateSchema.optional(),
  /** When the row was voided, and why. */
  voidedAt: dateSchema.optional(),
  voidReason: z.string().trim().optional(),
  /** Free-form finance note (transfer reference, …). */
  notes: z.string().trim().optional(),
});

export type ReferralReward = z.infer<typeof ReferralRewardSchema.model>;
export type ReferralRewardData = z.infer<typeof ReferralRewardSchema.data>;
export type ReferralRewardPartial = z.infer<typeof ReferralRewardSchema.partial>;
export type ReferralRewardQueryMany = QueryMany<ReferralReward>;
export type ReferralRewardQueryOne = QueryOne<ReferralReward>;
